/**
 * Navigation-flow manager.
 *
 * Opens a `nav:<pathname>` flow per route and installs it as the active flow
 * so every fetch / saga / WS handler that fires during the visit parents
 * its work under it. Without this, every mount-effect fetch is a root of
 * its own and the dashboard turns into a list of leaves with no tree.
 *
 * Lifecycle:
 *  - module init: open nav for the current location, set active.
 *  - history.pushState / replaceState: finalize old, open new.
 *  - popstate (back/forward): same.
 *  - pagehide / beforeunload: finalize the open one.
 *
 * History methods are patched at module init so the nav flow opens before
 * React renders — mount-effect fetches see the active flow on their very
 * first tick. Effect-based observers (useLocation + useEffect) lose that
 * race against deeply-nested mount fetches.
 */

import { finalizeFlow, setActiveFlow, startFlow } from "@yury-dymov/qpl-web";

let currentNavId: string | null = null;
let currentPath: string | null = null;
let installed = false;

function navName(pathname: string): string {
  return `nav:${pathname || "/"}`;
}

function openNav(pathname: string, search: string): void {
  if (currentNavId && currentPath === pathname) return;
  if (currentNavId) {
    finalizeFlow(currentNavId, "SUCCESS");
    currentNavId = null;
  }
  const handle = startFlow(navName(pathname), {
    pathname,
    search: search || undefined,
  });
  currentNavId = handle.id;
  currentPath = pathname;
  setActiveFlow(handle.id, navName(pathname));
}

function finalizeCurrent(outcome: "SUCCESS" | "CANCELED" = "SUCCESS"): void {
  if (!currentNavId) return;
  finalizeFlow(currentNavId, outcome);
  currentNavId = null;
  currentPath = null;
  setActiveFlow(null, null);
}

/**
 * Install the nav-flow manager. Idempotent — safe to call from multiple
 * entry points (the second call is a no-op).
 */
export function installNavFlow(): void {
  if (installed) return;
  installed = true;
  if (typeof window === "undefined") return;

  // Open the initial flow synchronously, before any React render runs.
  openNav(window.location.pathname, window.location.search);

  const origPushState = window.history.pushState.bind(window.history);
  const origReplaceState = window.history.replaceState.bind(window.history);

  // pushState arrives before the URL flips on the URL bar, but
  // window.location is already updated by the time the patched method
  // runs — so reading location after delegating is the canonical
  // post-navigation state.
  window.history.pushState = function patchedPushState(...args) {
    const ret = origPushState(...args);
    openNav(window.location.pathname, window.location.search);
    return ret;
  };
  window.history.replaceState = function patchedReplaceState(...args) {
    const ret = origReplaceState(...args);
    openNav(window.location.pathname, window.location.search);
    return ret;
  };

  window.addEventListener("popstate", () => {
    openNav(window.location.pathname, window.location.search);
  });

  // pagehide fires for both bfcache + real unload; covers both Safari and
  // Chrome reload paths. beforeunload would also fire but disables bfcache,
  // so prefer pagehide.
  window.addEventListener("pagehide", () => {
    finalizeCurrent("SUCCESS");
  });
}
